/**
 * Mirror backend reference-list canonicalisation (databricks_adapter.py) so the
 * Data Quality Results grid shows the same values that were validated.
 */

import type { KnownMapping, SchemaField } from "@/types";

const CODE_REFERENCE_VALUE_RE = /^[A-Za-z]{2,3}$/;

export function isCodeReferenceList(allowedValues: string[]): boolean {
  const vals = allowedValues.map((v) => v.trim()).filter(Boolean);
  if (vals.length === 0) return false;
  return vals.every((v) => CODE_REFERENCE_VALUE_RE.test(v) && v === v.toUpperCase());
}

export function allowedValuesAreUppercaseCanonical(allowedValues: string[]): boolean {
  const vals = allowedValues.map((v) => v.trim()).filter(Boolean);
  if (vals.length === 0) return false;
  return vals.every((v) => v === v.toUpperCase());
}

export function resolveReferenceListNormalizationMode(
  allowedValues: string[],
  valueFormat?: string | null
): "upper_only" | "map_lookup" {
  const fmt = valueFormat?.trim().toLowerCase();
  if (fmt === "label") return "map_lookup";
  if (fmt === "code") return "upper_only";
  if (isCodeReferenceList(allowedValues)) return "upper_only";
  if (allowedValuesAreUppercaseCanonical(allowedValues)) return "upper_only";
  return "map_lookup";
}

function buildReferenceListLookup(
  allowedValues: string[],
  isCodeList: boolean
): Map<string, string> {
  const lookup = new Map<string, string>();
  for (const value of allowedValues) {
    const key = value.trim().toUpperCase();
    if (!key) continue;
    lookup.set(key, isCodeList ? value.trim().toUpperCase() : value.trim());
  }
  return lookup;
}

/** Canonicalise one cell value for a reference-list rule (matches backend _canonical_reference_list_value). */
export function canonicalReferenceListValue(
  raw: unknown,
  allowedValues: string[],
  valueFormat?: string | null
): unknown {
  if (raw === null || raw === undefined) return raw;
  const text = String(raw);
  const stripped = text.trim();
  if (!stripped) return raw;

  const mode = resolveReferenceListNormalizationMode(allowedValues, valueFormat);
  if (mode === "upper_only") {
    const normalized = stripped.toUpperCase();
    const allowedUpper = new Set(allowedValues.map((v) => v.trim().toUpperCase()).filter(Boolean));
    if (allowedUpper.has(normalized)) return normalized;
    return raw;
  }

  const lookup = buildReferenceListLookup(allowedValues, false);
  return lookup.get(stripped.toUpperCase()) ?? raw;
}

function normalizeHeaderKey(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, "_").replace(/-/g, "_");
}

/** Resolve contract allowed-values for a grid header (field name, field id, or mapped target). */
export function resolveAllowedValuesForHeader(
  header: string,
  lookup: Map<string, string[]>,
  schemaFields?: SchemaField[],
  savedMappings?: KnownMapping[]
): string[] | undefined {
  const trimmed = header.trim();
  if (lookup.has(trimmed)) return lookup.get(trimmed);

  const lower = trimmed.toLowerCase();
  for (const [key, values] of lookup) {
    if (key.toLowerCase() === lower || normalizeHeaderKey(key) === normalizeHeaderKey(trimmed)) {
      return values;
    }
  }

  const sf = schemaFields?.find(
    (f) =>
      f.id.trim() === trimmed ||
      f.id.trim().toLowerCase() === lower ||
      f.fieldName?.trim() === trimmed ||
      f.fieldName?.trim().toLowerCase() === lower
  );
  if (sf?.fieldName) {
    const byName = lookup.get(sf.fieldName.trim());
    if (byName) return byName;
    for (const [key, values] of lookup) {
      if (key.toLowerCase() === sf.fieldName!.trim().toLowerCase()) return values;
    }
  }

  const mapping = savedMappings?.find(
    (m) =>
      m.targetField?.trim() === trimmed ||
      m.targetField?.trim().toLowerCase() === lower ||
      m.targetFieldId?.trim() === trimmed
  );
  if (mapping?.targetField) {
    const byTarget = lookup.get(mapping.targetField.trim());
    if (byTarget) return byTarget;
  }

  return undefined;
}

/**
 * Apply reference-list canonicalisation to every row in the Results grid.
 * Only columns with contract `is_in_list` allowed values are transformed.
 */
export function applyReferenceListNormalizationToGrid(
  rows: Record<string, unknown>[],
  headers: string[],
  columnAllowedValuesLookup: Map<string, string[]>,
  schemaFields?: SchemaField[],
  savedMappings?: KnownMapping[]
): Record<string, unknown>[] {
  if (rows.length === 0 || headers.length === 0 || columnAllowedValuesLookup.size === 0) {
    return rows;
  }

  const headerRules = headers.map((header) => ({
    header,
    allowedValues: resolveAllowedValuesForHeader(
      header,
      columnAllowedValuesLookup,
      schemaFields,
      savedMappings
    ),
  }));

  if (!headerRules.some((r) => r.allowedValues?.length)) return rows;

  return rows.map((row) => {
    const out: Record<string, unknown> = { ...row };
    for (const { header, allowedValues } of headerRules) {
      if (!allowedValues?.length || !(header in out)) continue;
      out[header] = canonicalReferenceListValue(out[header], allowedValues);
    }
    return out;
  });
}
